import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useQuery, gql } from '@apollo/client';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

// --- TYPE DEFINITION ---
interface Project {
  id: string;
  title: string;
  desc: string;
}

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(8, 0),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 2),
    },
  },
  headerTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    textAlign: 'center',
    textTransform: 'uppercase',
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.75rem',
    },
  },
  subHeader: {
    color: '#555',
    marginBottom: theme.spacing(6),
    textAlign: 'center',
    maxWidth: '600px',
    margin: '0 auto 48px auto',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(4),
      fontSize: '0.9rem',
      padding: theme.spacing(0, 2),
    },
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    textAlign: 'center',
    borderRadius: '16px',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    },
    [theme.breakpoints.down('xs')]: {
      height: 'auto',
      marginBottom: theme.spacing(1),
    }
  },
  iconBox: {
    backgroundColor: '#e3f2fd',
    color: '#26a69a',
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  multiLineEllipsis: {
    display: '-webkit-box',
    boxOrient: 'vertical',
    lineClamp: 2,
    '-webkit-line-clamp': 3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.text.secondary,
  },
  viewAllBtn: {
    marginTop: theme.spacing(15),
    padding: theme.spacing(1.5, 5),
    borderRadius: '50px',
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(6),
      width: '100%',
    },
  },
}));

// --- GRAPHQL QUERY ---
const GET_Projects = gql`
  query {
    project {
      data {
        attributes {
          data(pagination: { page: 1, pageSize: 4}) {
            id
            description
            title
          }
        }
      }
    }
  }
`;

export default function ProjectsHome() {
  const classes = useStyles();
  
  // FIX 1: Explicitly tell useState that this is an array of 'Project' objects
  const [projects, setProjects] = useState<Project[]>([]);

  // Fetch Data
  const { loading, error } = useQuery(GET_Projects, {
    onCompleted: (data: any) => {
      // Safely access nested data
      const rawData = data?.project?.data?.attributes?.data || [];
      
      // FIX 2: Type 'each' as 'any' to bypass strict check on API response
      const formattedData = rawData.map((each: any) => ({
        id: each.id,
        title: each.title,
        desc: each.description,
      }));
      
      setProjects(formattedData);
    },
  });

  // FIX 3: Type the 'id' parameter
  const handleCardClick = (id: string) => {
     window.location.href = `/projects/${id}`;
  };

  const handleViewAll = () => {
     window.location.href = '/projects'; 
  };

  return (
    <div className={classes.section}>
      <Container maxWidth="lg">
        <Typography variant="h4" className={classes.headerTitle}>
          Projects & Programs
        </Typography>
        <Typography variant="subtitle1" className={classes.subHeader}>
          Empowering communities through sustainable initiatives and dedicated service.
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" my={5}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" align="center">
            Error loading projects.
          </Typography>
        )}

        {!loading && !error && (
          <Grid container spacing={4}>
            {/* 'project' is now correctly typed, so .id and .title will work */}
            {projects.map((project, index) => (
              <Grid item xs={12} sm={6} md={3} key={project.id}>
                <Card 
                  className={classes.card} 
                  elevation={1} 
                  onClick={() => handleCardClick(project.id)}
                >
                  <div className={classes.iconBox}>
                    {index + 1}
                  </div>
                  
                  <CardContent style={{ padding: 0, width: '100%' }}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      {project.title}
                    </Typography>
                    
                    <Typography variant="body2" className={classes.multiLineEllipsis}>
                      {project.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Box textAlign="center">
          <Button 
            variant="contained" 
            color="primary" 
            className={classes.viewAllBtn}
            endIcon={<ArrowForwardIcon />}
            onClick={handleViewAll}
          >
            View All Programs
          </Button>
        </Box>
      </Container>
    </div>
  );
}